import { ProcessingResponse, ProcessingStatus } from '../types/asset';
import { useState, useEffect, useRef } from 'react';
import { appendTokenParam, getToken } from '../auth/keycloak';

interface AssetStatus {
  assetId: string;
  state: ProcessingStatus;
  progress?: number;
  error?: string;
  metadata?: Record<string, any>;
}

interface AggregatedStatus {
  project_id: number;
  status: {
    total_assets: number;
    processed_assets: number;
    failed_assets: number;
    in_progress_assets: number;
    status_summary: {
      pending: number;
      processing: number;
      completed: number;
      failed: number;
      stopped: number;
    };
    token_usage?: any;
  };
  assets: any[];
  lastUpdateTime: number;
}

/**
 * StatusAggregator - Solves the async processing status update problem
 * 
 * Problem: When processing assets asynchronously, status updates arrive out of order,
 * causing UI counts to jump around (e.g., 15 -> 13 -> 15) which is confusing for users.
 * 
 * Solution: This aggregator debounces and aggregates status updates to provide:
 * 1. Stable, predictable UI updates that don't jump around
 * 2. Real-time progress tracking without confusing count changes
 * 3. Configurable debounce delay for different use cases
 * 4. Efficient aggregation that only sends meaningful changes
 * 
 * Usage:
 * - Individual status updates are aggregated and debounced
 * - Batch updates are sent immediately (for initial loads)
 * - UI receives stable, consistent progress information
 * - Async processing efficiency is maintained on the backend
 */
class StatusAggregator {
  private projectId: string;
  private debounceTimeout: NodeJS.Timeout | null = null;
  private debounceDelay: number = 500; // 500ms debounce
  private callback: ((data: ProcessingResponse) => void) | null = null;
  private currentStatus: AggregatedStatus | null = null;
  private pendingUpdates: Map<string, any> = new Map();
  private lastSentStatus: AggregatedStatus | null = null;

  constructor(projectId: string, callback: (data: ProcessingResponse) => void) {
    this.projectId = projectId;
    this.callback = callback;
  }

  public updateStatus(data: ProcessingResponse): void {
    const now = Date.now();
    
    // If this is a complete status update, force immediate update
    const isCompleteUpdate = data.status.processed_assets === data.status.total_assets && 
                            data.status.in_progress_assets === 0;
    
    if (isCompleteUpdate) {
      console.log('[StatusAggregator] Complete status update detected, forcing immediate update');
      this.currentStatus = {
        project_id: data.project_id,
        status: data.status,
        assets: data.assets || [],
        lastUpdateTime: now
      };
      
      // Clear any pending debounce
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }
      
      // Send immediately
      this.sendAggregatedUpdate();
      return;
    }
    
    // For non-complete updates, use debouncing
    this.currentStatus = {
      project_id: data.project_id,
      status: data.status,
      assets: data.assets || [],
      lastUpdateTime: now
    };

    // Store individual asset updates
    if (data.assets && data.assets.length > 0) {
      data.assets.forEach((asset: any) => {
        this.pendingUpdates.set(asset.id, asset);
      });
    }

    this.debounceUpdate();
  }

  private debounceUpdate(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.sendAggregatedUpdate();
    }, this.debounceDelay);
  }

  private sendAggregatedUpdate(): void {
    if (!this.currentStatus || !this.callback) {
      return;
    }

    // Check if this is a significant change
    if (!this.hasSignificantChanges()) {
      return;
    }

    // Prepare the aggregated status
    const aggregatedStatus: ProcessingResponse = {
      project_id: this.currentStatus.project_id,
      status: {
        total_assets: this.currentStatus.status.total_assets,
        processed_assets: this.currentStatus.status.processed_assets,
        failed_assets: this.currentStatus.status.failed_assets,
        in_progress_assets: this.currentStatus.status.in_progress_assets,
        status_summary: this.currentStatus.status.status_summary,
        token_usage: this.currentStatus.status.token_usage // Include token usage from the status
      },
      assets: this.currentStatus.assets
    };

    console.log('[StatusAggregator] Sending aggregated update:', {
      total_assets: aggregatedStatus.status.total_assets,
      processed_assets: aggregatedStatus.status.processed_assets,
      failed_assets: aggregatedStatus.status.failed_assets,
      in_progress_assets: aggregatedStatus.status.in_progress_assets,
      has_token_usage: !!aggregatedStatus.status.token_usage
    });

    // Send the update
    this.callback(aggregatedStatus);
    
    // Update last sent status
    this.lastSentStatus = { ...this.currentStatus };
  }

  private hasSignificantChanges(): boolean {
    if (!this.lastSentStatus) return true;

    const current = this.currentStatus!;
    const last = this.lastSentStatus;

    // Check if counts have changed significantly
    return (
      current.status.processed_assets !== last.status.processed_assets ||
      current.status.failed_assets !== last.status.failed_assets ||
      current.status.in_progress_assets !== last.status.in_progress_assets ||
      current.status.total_assets !== last.status.total_assets ||
      current.assets.length !== last.assets.length
    );
  }

  public forceUpdate(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.sendAggregatedUpdate();
  }

  public setDebounceDelay(delayMs: number): void {
    this.debounceDelay = delayMs;
  }

  public cleanup(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    this.pendingUpdates.clear();
    this.currentStatus = null;
    this.lastSentStatus = null;
  }

  public getCurrentProgress(): { processed: number; total: number; percentage: number } {
    if (!this.currentStatus) {
      return { processed: 0, total: 0, percentage: 0 };
    }

    const total = this.currentStatus.status.total_assets;
    const processed = this.currentStatus.status.processed_assets + this.currentStatus.status.failed_assets;
    const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

    return { processed, total, percentage };
  }
}

interface WebSocketHook {
  socket: WebSocket | null;
  isConnected: boolean;
  sendMessage: (message: any) => void;
  connect: () => void;
  disconnect: () => void;
}

export const useWebSocket = (url?: string): WebSocketHook => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  
  // Default WebSocket URL
  const defaultUrl = url || (() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = process.env.NODE_ENV === 'production' ? window.location.port : '8003';
    return `${protocol}//${host}:${port}/ws`;
  })();

  const connect = async () => {
    try {
      const ws = new WebSocket(appendTokenParam(defaultUrl, await getToken()));
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          // Handle different message types
          if (data.type === 'processing_update') {
            // Emit custom event for processing updates
            window.dispatchEvent(new CustomEvent('processing_update', { detail: data }));
          } else if (data.type === 'queue_update') {
            // Emit custom event for queue updates
            window.dispatchEvent(new CustomEvent('queue_update', { detail: data }));
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      socketRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setIsConnected(false);
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      setIsConnected(false);
    }
  };

  const sendMessage = (message: any) => {
    if (socketRef.current && isConnected) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  };

  useEffect(() => {
    // Auto-connect on mount
    connect();
    
    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, []);

  // Auto-reconnect on connection loss
  useEffect(() => {
    if (!isConnected && socketRef.current === null) {
      const timeout = setTimeout(() => {
        console.log('Attempting to reconnect WebSocket...');
        connect();
      }, 5000); // Wait 5 seconds before reconnecting
      
      return () => clearTimeout(timeout);
    }
  }, [isConnected]);

  return {
    socket: socketRef.current,
    isConnected,
    sendMessage,
    connect,
    disconnect
  };
};

// WebSocket event listeners for the EnhancedAssetProcessor
export const useWebSocketEvents = () => {
  useEffect(() => {
    const handleProcessingUpdate = (event: CustomEvent) => {
      // This will be handled by the component that uses this hook
      console.log('Processing update received:', event.detail);
    };

    const handleQueueUpdate = (event: CustomEvent) => {
      // This will be handled by the component that uses this hook
      console.log('Queue update received:', event.detail);
    };

    window.addEventListener('processing_update', handleProcessingUpdate as EventListener);
    window.addEventListener('queue_update', handleQueueUpdate as EventListener);

    return () => {
      window.removeEventListener('processing_update', handleProcessingUpdate as EventListener);
      window.removeEventListener('queue_update', handleQueueUpdate as EventListener);
    };
  }, []);
};

export class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout = 1000;
  private pingInterval: NodeJS.Timeout | null = null;
  private currentProjectId: string | null = null;
  private connectionStatusHandlers: ((connected: boolean) => void)[] = [];
  private errorHandlers: ((error: string) => void)[] = [];
  private isReconnecting = false;
  private wsUrl: string;
  private connectionStatusCallback?: (connected: boolean) => void;
  private statusUpdateCallback: ((data: ProcessingResponse) => void) | null = null;
  private completeCallback: (() => void) | null = null;
  private errorCallback: ((error: string) => void) | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageQueue: any[] = [];
  private isConnectionReady = false;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private connectionEstablished = false;
  private connectionAttempts = 0;
  private maxConnectionAttempts = 3;
  private connectionRetryDelay = 1000;
  private connectionCheckInterval: NodeJS.Timeout | null = null;
  private isDisconnecting = false;
  private isConnecting = false;
  private disconnectPromise: Promise<void> | null = null;
  private disconnectResolve: (() => void) | null = null;
  private connectionLock: Promise<void> | null = null;
  private connectionLockResolve: (() => void) | null = null;
  private debugMode = true;
  private connectionPromise: Promise<void> | null = null;
  private lastPongTime = 0;
  private _isConnected = false;
  private autoDisconnectOnComplete = false;
  private statusAggregator: StatusAggregator | null = null;

  public getIsConnected(): boolean {
    return this._isConnected;
  }

  public isSocketConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  public getCurrentProjectId(): string | null {
    return this.currentProjectId;
  }

  public setAutoDisconnectOnComplete(enabled: boolean): void {
    this.autoDisconnectOnComplete = enabled;
  }

  public getAutoDisconnectOnComplete(): boolean {
    return this.autoDisconnectOnComplete;
  }

  public setStatusUpdateDebounce(delayMs: number): void {
    if (this.statusAggregator) {
      this.statusAggregator.setDebounceDelay(delayMs);
    }
  }

  private constructor() {
    this.connect = this.connect.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
    this.handleClose = this.handleClose.bind(this);
    
    // Use environment variables for WebSocket configuration
    if (process.env.REACT_APP_WS_URL) {
      this.wsUrl = process.env.REACT_APP_WS_URL;
    } else if (process.env.NODE_ENV === 'production') {
      // In production (when served through nginx), use the same hostname
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = window.location.port ? `:${window.location.port}` : '';
      this.wsUrl = `${protocol}//${host}${port}/ws/processing`;
    } else {
      // In development, use localhost
      this.wsUrl = 'ws://localhost:8003/ws/processing';
    }
    
    this.debugLog('WebSocket service initialized with URL:', this.wsUrl);
    this.debugLog('Environment:', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_WS_URL: process.env.REACT_APP_WS_URL,
      host: window.location.hostname,
      port: window.location.port ? `:${window.location.port}` : 'no port'
    });
    
    // Set default auto-disconnect behavior
    this.autoDisconnectOnComplete = false;
  }

  private debugLog(...args: any[]) {
    if (this.debugMode) {
      console.log('[WebSocket Debug]', ...args);
    }
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  private async acquireConnectionLock(): Promise<void> {
    if (this.connectionLock) {
      console.log('[WebSocket] Waiting for connection lock to be released');
      await this.connectionLock;
    }
    this.connectionLock = new Promise<void>((resolve) => {
      this.connectionLockResolve = resolve;
    });
  }

  private releaseConnectionLock(): void {
    if (this.connectionLockResolve) {
      this.connectionLockResolve();
      this.connectionLock = null;
      this.connectionLockResolve = null;
    }
  }

  public onConnectionStatusChange(callback: (connected: boolean) => void): () => void {
    this.connectionStatusHandlers.push(callback);
    // Return cleanup function
    return () => {
      const index = this.connectionStatusHandlers.indexOf(callback);
      if (index > -1) {
        this.connectionStatusHandlers.splice(index, 1);
      }
    };
  }

  private notifyConnectionStatusChange(connected: boolean): void {
    this.connectionStatusHandlers.forEach(handler => handler(connected));
  }

  private getWebSocketUrl(projectId: string): string {
    // Use environment variable if set
    if (process.env.REACT_APP_WS_URL) {
      const url = `${process.env.REACT_APP_WS_URL}/ws/processing/${projectId}`;
      console.log('[WebSocket] Using environment WS_URL:', url);
      return url;
    }
    
    // Always use relative path when served through nginx (Docker)
    // This will be proxied to the search-api service
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    const url = `${protocol}//${host}${port}/ws/processing/${projectId}`;
    console.log('[WebSocket] Constructed URL:', url);
    console.log('[WebSocket] Current location:', window.location.href);
    console.log('[WebSocket] Protocol:', protocol);
    console.log('[WebSocket] Host:', host);
    console.log('[WebSocket] Port:', port);
    return url;
  }

  public async connect(projectId: string): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Set current project ID
    this.currentProjectId = projectId;

    // Initialize status aggregator for this project
    if (this.statusUpdateCallback) {
      this.statusAggregator = new StatusAggregator(projectId, this.statusUpdateCallback);
    }

    // Keycloak access token for backend WebSocket authentication.
    const authToken = await getToken();

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        const wsUrl = appendTokenParam(this.getWebSocketUrl(projectId), authToken);
        console.debug('[WebSocket Debug] Connecting to:', wsUrl);

        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          console.log('[WebSocket] Connection established successfully');
          console.log('[WebSocket] Socket ready state:', this.socket?.readyState);
          this._isConnected = true;
          this.notifyConnectionStatusChange(true);
          this.startPingInterval();
          resolve();
        };

        this.socket.onmessage = (event) => {
          console.debug('[WebSocket Debug] Raw message received:', event.data);
          this.handleMessage(event);
        };

        this.socket.onclose = (event) => {
          console.log('[WebSocket] Connection closed:', event.code, event.reason);
          console.log('[WebSocket] Close details:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            timestamp: new Date().toISOString()
          });
          
          this._isConnected = false;
          this.notifyConnectionStatusChange(false);
          this.stopPingInterval();
          this.connectionPromise = null;
          
          // Don't attempt to reconnect if we're intentionally disconnecting
          if (this.isDisconnecting) {
            console.debug('[WebSocket Debug] Intentional disconnect, not attempting to reconnect');
            return;
          }
          
          // Handle different close codes
          if (event.code === 1006) {
            console.warn('[WebSocket Debug] Abnormal closure (1006) - likely timeout or network issue');
            // Attempt to reconnect for abnormal closures
            if (this.currentProjectId && !this.isDisconnecting) {
              console.log('[WebSocket Debug] Attempting to reconnect after abnormal closure...');
              setTimeout(() => {
                this.handleConnectionError();
              }, 2000);
            }
          } else if (event.code === 1000) {
            console.log('[WebSocket Debug] Normal closure (1000)');
          } else {
            console.warn('[WebSocket Debug] Unexpected closure code:', event.code);
            // Attempt to reconnect for other unexpected closures
            if (this.currentProjectId && !this.isDisconnecting) {
              console.log('[WebSocket Debug] Attempting to reconnect after unexpected closure...');
              setTimeout(() => {
                this.handleConnectionError();
              }, 3000);
            }
          }
          
          if (this.errorCallback) {
            this.errorCallback(`WebSocket connection closed (code: ${event.code})`);
          }
        };

        this.socket.onerror = (error) => {
          console.error('[WebSocket] WebSocket error:', error);
          console.log('[WebSocket] Error details:', {
            type: error.type,
            target: error.target,
            timestamp: new Date().toISOString()
          });
          this._isConnected = false;
          this.notifyConnectionStatusChange(false);
          this.stopPingInterval();
          this.connectionPromise = null;
          reject(error);
        };
      } catch (error) {
        console.error('[WebSocket Debug] Error setting up WebSocket:', error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  private async cleanupSocket(): Promise<void> {
    if (!this.socket) return;

    const socket = this.socket;
    this.socket = null;

    try {
      // Remove event listeners first
      socket.onopen = null;
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;

      // Only close if not already closing or closed
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        console.log('[WebSocket] Closing socket');
        socket.close(1000, 'Normal closure');
        
        // Wait for socket to close
        await new Promise<void>((resolve) => {
          const checkClose = () => {
            if (socket.readyState === WebSocket.CLOSED) {
              resolve();
            } else {
              setTimeout(checkClose, 100);
            }
          };
          checkClose();
        });
      }
    } catch (error) {
      console.error('[WebSocket] Error during socket cleanup:', error);
    }
  }

  private cleanupTimeouts() {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  private startConnectionCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }
    this.connectionCheckInterval = setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) {
        console.error('WebSocket connection check failed');
        this.handleConnectionError();
      }
    }, 30000); // Check every 30 seconds
  }

  private handleConnectionError() {
    if (this.isDisconnecting) {
      this.debugLog('Not attempting to reconnect while disconnecting');
      return;
    }

    if (this.isReconnecting) {
      this.debugLog('Already attempting to reconnect, skipping additional attempt');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.debugLog('Max reconnection attempts reached');
      this.errorCallback?.('Failed to reconnect after multiple attempts');
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    this.debugLog(`Attempting to reconnect (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(async () => {
      try {
        // Check again if we're still disconnecting
        if (this.isDisconnecting) {
          this.debugLog('Disconnecting flag set during reconnection attempt, aborting');
          this.isReconnecting = false;
          return;
        }
        
        await this.connect(this.currentProjectId || '');
        this.isReconnecting = false;
      } catch (error) {
        this.debugLog('Reconnection attempt failed:', error);
        this.handleConnectionError();
      }
    }, this.reconnectTimeout);
  }

  private processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const data = this.messageQueue.shift();
      if (data.type === 'status_update') {
        this.statusUpdateCallback?.(data.data);
      } else if (data.type === 'error') {
        this.errorCallback?.(data.message);
      } else if (data.type === 'complete') {
        this.completeCallback?.();
      }
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this._isConnected && this.socket?.readyState === WebSocket.OPEN) {
        console.debug('[WebSocket Debug] Sending ping');
        this.socket.send('ping');
      }
    }, 15000); // Send ping every 15 seconds instead of 30
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
        // Handle ping/pong messages first (plain text)
        if (event.data === 'pong') {
            console.debug('[WebSocket Debug] Received pong');
            return;
        }
        
        const message = JSON.parse(event.data);
        console.log('[WebSocket] Received message:', message);

        // Handle connection status message
        if (message.type === 'connection_status') {
            console.log('[WebSocket] Connection status:', message.data.status);
            return;
        }

        // Handle batch status update
        if (message.statuses) {
            console.log('[WebSocket] Processing batch status update');
            const assets = message.statuses.map((status: any) => ({
                id: status.asset_id,
                title: `Asset ${status.asset_id}`,
                status: status.state,
                progress: status.progress,
                error: status.error,
                token_usage: status.token_usage,
                metadata: status.metadata
            }));

            const statusSummary = {
                total_assets: assets.length,
                processed_assets: assets.filter((a: any) => a.status === 'completed').length,
                failed_assets: assets.filter((a: any) => a.status === 'failed').length,
                in_progress_assets: assets.filter((a: any) => a.status === 'processing').length,
                status_summary: {
                    pending: assets.filter((a: any) => a.status === 'pending').length,
                    processing: assets.filter((a: any) => a.status === 'processing').length,
                    completed: assets.filter((a: any) => a.status === 'completed').length,
                    failed: assets.filter((a: any) => a.status === 'failed').length,
                    stopped: assets.filter((a: any) => a.status === 'stopped').length
                },
                token_usage: message.token_usage
            };

            if (this.statusUpdateCallback) {
                this.statusUpdateCallback({
                    project_id: parseInt(message.project_id),
                    status: statusSummary,
                    assets: assets
                });
            }
            return;
        }

        // Handle individual status update
        if (message.type === 'status_update') {
            console.log('[WebSocket] Processing individual status update');
            const asset = {
                id: message.data.asset_id,
                title: `Asset ${message.data.asset_id}`,
                status: message.data.status,
                progress: message.data.progress,
                error: message.data.error,
                token_usage: message.data.token_usage
            };

            // If we have a status summary in the message, use it
            const statusSummary = message.status_summary || {
                total_assets: 1,
                processed_assets: message.data.status === 'completed' ? 1 : 0,
                failed_assets: message.data.status === 'failed' ? 1 : 0,
                in_progress_assets: message.data.status === 'processing' ? 1 : 0,
                status_summary: {
                    pending: message.data.status === 'pending' ? 1 : 0,
                    processing: message.data.status === 'processing' ? 1 : 0,
                    completed: message.data.status === 'completed' ? 1 : 0,
                    failed: message.data.status === 'failed' ? 1 : 0,
                    stopped: message.data.status === 'stopped' ? 1 : 0
                }
            };

            // Include token usage from status_summary if available
            if (message.status_summary?.token_usage) {
                statusSummary.token_usage = message.status_summary.token_usage;
                console.log('[WebSocket] Including token usage from status_summary:', statusSummary.token_usage);
            }
            
            // Also check for token_usage in the data field (for individual asset updates)
            if (message.data?.token_usage) {
                statusSummary.token_usage = message.data.token_usage;
                console.log('[WebSocket] Including token usage from data field:', statusSummary.token_usage);
            }

            const statusData = {
                project_id: parseInt(message.project_id),
                status: statusSummary,
                assets: [asset]
            };

            // Handle complete message - bypass aggregator for final token usage
            if (message.data.status === 'complete') {
                console.log('[WebSocket] Processing complete - FINAL MESSAGE');
                
                // For final complete messages, bypass aggregator and send directly
                // This ensures we get the final token usage without aggregation interference
                if (this.statusUpdateCallback) {
                    console.log('[WebSocket] Sending final complete message directly to component');
                    this.statusUpdateCallback(statusData);
                }
                
                // Call complete callback
                if (this.completeCallback) {
                    setTimeout(() => {
                        if (this.completeCallback) {
                            this.completeCallback();
                        }
                    }, 100);
                }
                
                // Auto-disconnect after completion if enabled
                if (this.autoDisconnectOnComplete) {
                    console.log('[WebSocket] Auto-disconnecting after completion');
                    setTimeout(async () => {
                        try {
                            console.log('[WebSocket] Executing auto-disconnect...');
                            await this.disconnect();
                            console.log('[WebSocket] Auto-disconnect completed successfully');
                        } catch (error) {
                            console.error('[WebSocket] Error during auto-disconnect:', error);
                        }
                    }, 5000);
                } else {
                    console.log('[WebSocket] Auto-disconnect disabled, keeping connection open');
                }
                return;
            }

            // Use status aggregator for individual updates to prevent UI jumping
            if (this.statusAggregator) {
                this.statusAggregator.updateStatus(statusData);
            } else if (this.statusUpdateCallback) {
                // Fallback to direct callback if aggregator not available
                this.statusUpdateCallback(statusData);
            }
            return;
        }

        console.log('[WebSocket] Unhandled message type:', message.type);
    } catch (error) {
        console.error('[WebSocket] Error handling message:', error);
    }
  }

  private handleClose() {
    this._isConnected = false;
    this.stopPingInterval();
    this.cleanupTimeouts();
    if (this.errorCallback) {
      this.errorCallback('WebSocket connection closed');
    }
  }

  public async disconnect(): Promise<void> {
    return new Promise((resolve) => {
      if (this.socket) {
        const socket = this.socket;
        
        // Set flag to prevent reconnection attempts
        this.isDisconnecting = true;
        
        // Clean up timeouts and intervals
        this.cleanupTimeouts();
        
        socket.onclose = () => {
          console.debug('[WebSocket Debug] Disconnected successfully');
          this._isConnected = false;
          this.stopPingInterval();
          this.socket = null;
          this.connectionPromise = null;
          this.isDisconnecting = false;
          this.currentProjectId = null;
          
          // Clean up status aggregator
          if (this.statusAggregator) {
            this.statusAggregator.cleanup();
            this.statusAggregator = null;
          }
          
          resolve();
        };
        
        // Only close if not already closing or closed
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          console.debug('[WebSocket Debug] Closing socket');
          socket.close(1000, 'Normal closure');
        } else {
          console.debug('[WebSocket Debug] Socket already closed or closing');
          this._isConnected = false;
          this.socket = null;
          this.connectionPromise = null;
          this.isDisconnecting = false;
          this.currentProjectId = null;
          resolve();
        }
      } else {
        console.debug('[WebSocket Debug] No socket to disconnect');
        this.isDisconnecting = false;
        resolve();
      }
    });
  }

  public onStatusUpdate(callback: (data: ProcessingResponse) => void): void {
    this.statusUpdateCallback = callback;
    
    // Initialize status aggregator if we have a project ID
    if (this.currentProjectId) {
      this.statusAggregator = new StatusAggregator(this.currentProjectId, callback);
    }
  }

  public onComplete(callback: () => void): void {
    this.completeCallback = callback;
  }

  public onError(callback: (error: string) => void): void {
    this.errorCallback = callback;
  }

  public async forceDisconnect(): Promise<void> {
    console.log('[WebSocket] Force disconnecting...');
    this.autoDisconnectOnComplete = false; // Temporarily disable auto-disconnect
    await this.disconnect();
    this.autoDisconnectOnComplete = true; // Re-enable auto-disconnect
  }

  private sendMessage(message: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }
}

export const websocketService = WebSocketService.getInstance();